import { DataDisplay, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { ControlProps, UISchemaElement } from "@jsonforms/core";
import { JsonFormsDispatch, withJsonFormsControlProps } from "@jsonforms/react";
import React from "react";

import {
  handleMissingAndBlankData,
  strToKebabCase,
  getKeyFromScope,
} from "../utils";

interface Dictionary<T> {
  [Key: string]: T;
}

function processOutput(
  element: any,
  result: Dictionary<object>,
  rowNum: number,
  componentProps: ControlProps
) {
  // if there are child elements/controls to dispatch, process those
  const children = element.options.detail?.elements;
  if (children?.length > 0) {
    return (
      <span className={css(styles.dataDisplayTableCell)}>
        {children.map((elem: UISchemaElement, index: number) => {
          return (
            <JsonFormsDispatch
              key={index}
              schema={componentProps.schema}
              uischema={elem}
              path={`${componentProps.path}.${rowNum}`}
            />
          );
        })}
      </span>
    );
  }

  // get data key from scope
  // ie: get "sources" from #/properties/sources
  const key = getKeyFromScope(element.scope);
  const data = handleMissingAndBlankData(result[key]);

  return <Text className={css(styles.dataDisplayTableCell)}>{data}</Text>;
}

function prepareTableRow(
  result: any,
  rowNum: number,
  componentProps: ControlProps
) {
  const tableRow: Dictionary<object> = {};
  const options = componentProps.uischema.options || {};

  options?.detail.elements.forEach(
    (element: UISchemaElement, index: number) => {
      const columnKey = `col${index.toString()}`;
      tableRow[columnKey] = processOutput(
        element,
        result,
        rowNum,
        componentProps
      );
    }
  );
  return tableRow;
}

export function DataDisplayTable(props: ControlProps) {
  if (!props.visible) {
    return null;
  }

  if (!props.data) {
    return null;
  }

  const options = props.uischema.options || {};
  let results = props.data;

  if (!Array.isArray(results)) {
    results = [results];
  }

  return (
    <DataDisplay
      key={`${strToKebabCase(options.detail.elements[0].label || "blank")}`}
      aria-label={`${options.detail.elements[0].label} results`}
      testId={`${strToKebabCase(options.detail.elements[0].label || "blank")}-data-table`}
      columns={options.detail.elements.map((element: any, index: number) => ({
        isRowHeader: index === 0,
        key: `col${index.toString()}`,
        name: element.label,
        width: element.width ? element.width : null,
        columnContentBold: !!element.options?.columnContentBold,
        headerBadge: element.options?.headerBadge,
      }))}
      rows={results.map((result: any, index: number) => ({
        key: index.toString(),
        ...prepareTableRow(result, index, props),
      }))}
      renderTopMargin={false}
    />
  );
}

const styles = {
  dataDisplayTableCell: css({
    wordBreak: "break-word",
    fontSize: "var(--font-size-100)",
  }),
};

export const DataDisplayTableRenderer =
  withJsonFormsControlProps(DataDisplayTable);
